import { ArrowLeft, FileText } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProfitDetail, getProfitDetailCustom } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportSalesDetail.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

const PERIODS = [
    { key: 'custom', label: 'Tuỳ chọn' },
    { key: '7days', label: '7 ngày' },
    { key: 'thismonth', label: 'Tháng này' },
    { key: 'lastmonth', label: 'Tháng trước' },
    { key: 'thisquarter', label: 'Quý này' },
    { key: 'thisyear', label: 'Năm này' },
];

const VIEW_MODES = [
    { key: 'day', label: 'Theo ngày' },
    { key: 'week', label: 'Theo tuần' },
    { key: 'month', label: 'Theo tháng' },
];

function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getQuarterRange() {
    const now = new Date();
    const qStart = Math.floor(now.getMonth() / 3) * 3;
    const start = new Date(now.getFullYear(), qStart, 1);
    const end = new Date(now.getFullYear(), qStart + 3, 0);
    return { startDate: fmtDate(start), endDate: fmtDate(end) };
}

function formatDateDisplay(dateStr, label) {
    if (label) return label;
    if (!dateStr) return '—';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
}

function formatNum(v) {
    return (v || 0).toLocaleString('vi-VN');
}

export default function ReportSalesDetail() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.REPORT.SALES_DETAIL_VIEW);
    const canViewBillDetail = hasPermission(PERMISSIONS.REPORT.REVENUE_BILL_DETAIL_VIEW);
    const [searchParams] = useSearchParams();

    const urlPeriod = searchParams.get('period');
    const urlStart = searchParams.get('startDate');
    const urlEnd = searchParams.get('endDate');

    const initQuarter = getQuarterRange();

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(urlPeriod || 'custom');
    const [viewMode, setViewMode] = useState('day');
    const [startDate, setStartDate] = useState(urlStart || initQuarter.startDate);
    const [endDate, setEndDate] = useState(urlEnd || initQuarter.endDate);
    const [detailData, setDetailData] = useState([]);
    const [summary, setSummary] = useState({ totalRevenue: 0, totalCost: 0, totalProfit: 0 });
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1 });

    const PAGE_SIZE = 30;

    // Auto-adjust viewMode based on period + reset page
    useEffect(() => {
        let newMode = viewMode;
        if (period === 'custom') {
            newMode = 'day';
        } else if ((period === 'thisquarter' || period === 'thisyear') && viewMode === 'day') {
            newMode = 'week';
        } else if ((period === 'today' || period === 'yesterday') && viewMode !== 'day') {
            newMode = 'day';
        }
        if (newMode !== viewMode) setViewMode(newMode);
        setPage(1);
    }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchIdRef = useRef(0);

    const fetchData = useCallback(async (p = page) => {
        if (!canView) { setLoading(false); return; }
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            let res;
            if (period === 'custom') {
                res = await getProfitDetailCustom({ startDate, endDate, page: p, limit: PAGE_SIZE });
            } else {
                res = await getProfitDetail({ period, viewMode, page: p, limit: PAGE_SIZE });
            }
            // Bỏ qua response cũ nếu đã có fetch mới
            if (fetchId !== fetchIdRef.current) return;
            setDetailData(res.data?.detailData || []);
            setSummary({
                totalRevenue: res.data?.totalRevenue || 0,
                totalCost: res.data?.totalCost || 0,
                totalProfit: res.data?.totalProfit || 0,
            });
            if (res.data?.pagination) {
                setPagination(res.data.pagination);
            } else {
                const total = (res.data?.detailData || []).length;
                setPagination({ totalItems: total, totalPages: 1 });
            }
        } catch (err) {
            if (fetchId !== fetchIdRef.current) return;
            console.error('Error fetching profit detail:', err);
            setDetailData([]);
            setSummary({ totalRevenue: 0, totalCost: 0, totalProfit: 0 });
        } finally {
            if (fetchId === fetchIdRef.current) setLoading(false);
        }
    }, [canView, period, viewMode, startDate, endDate, page]);

    useEffect(() => {
        fetchData(page);
    }, [fetchData, page]);

    // Reset page khi đổi filter
    const handlePeriodChange = (p) => { setPeriod(p); setPage(1); };

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
    };

    const isViewModeDisabled = (key) => {
        if (key === 'day' && (period === 'thisquarter' || period === 'thisyear')) return true;
        if ((key === 'week' || key === 'month') && (period === 'today' || period === 'yesterday')) return true;
        return false;
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <h1 className={styles.title}>Chi tiết lợi nhuận</h1>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.periodTabs}>
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            className={`${styles.periodTab}${period === p.key ? ` ${styles.periodTabActive}` : ''}`}
                            onClick={() => handlePeriodChange(p.key)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {period === 'custom' && (
                    <>
                        <div className={styles.customDateRow}>
                            <div className={styles.dateGroup}>
                                <span className={styles.dateLabel}>Từ ngày:</span>
                                <input type="date" className={styles.dateInput} value={startDate} onChange={handleStartDateChange} />
                            </div>
                            <div className={styles.dateGroup}>
                                <span className={styles.dateLabel}>Đến ngày:</span>
                                <input type="date" className={styles.dateInput} value={endDate} onChange={handleEndDateChange} />
                            </div>
                        </div>
                        {canViewBillDetail && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    className={styles.periodTab}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0098F6', color: '#fff', borderColor: '#0098F6', borderRadius: 8 }}
                                    onClick={() => navigate(`/admin/reports/bills-detail?startDate=${startDate}&endDate=${endDate}`)}
                                >
                                    <FileText size={14} /> Chi tiết từng hoá đơn
                                </button>
                            </div>
                        )}
                    </>
                )}

                {period !== 'custom' && (
                    <div className={styles.viewModeTabs}>
                        {VIEW_MODES.map((v) => (
                            <button
                                key={v.key}
                                className={`${styles.viewModeTab}${viewMode === v.key ? ` ${styles.viewModeTabActive}` : ''}`}
                                onClick={() => { if (!isViewModeDisabled(v.key)) { setViewMode(v.key); setPage(1); } }}
                                disabled={isViewModeDisabled(v.key)}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Table */}
            <div className={styles.tableCard}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải dữ liệu...
                    </div>
                ) : detailData.length === 0 ? (
                    <div className={styles.empty}>Không có dữ liệu trong khoảng thời gian này</div>
                ) : (
                    <>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 50, textAlign: 'center' }}>STT</th>
                                <th style={{ width: 100 }}>Ngày</th>
                                <th>Doanh thu</th>
                                <th>Giá vốn</th>
                                <th>Lợi nhuận</th>
                            </tr>
                            <tr className={styles.summaryRow}>
                                <td colSpan={2} className={styles.summaryLabel}>Tổng</td>
                                <td className={styles.summaryValue}>{formatNum(summary.totalRevenue)}</td>
                                <td className={styles.summaryValue}>{formatNum(summary.totalCost)}</td>
                                <td className={styles.summaryValue}>{formatNum(summary.totalProfit)}</td>
                            </tr>
                        </thead>
                        <tbody>
                            {detailData.map((item, idx) => (
                                <tr key={idx}>
                                    <td style={{ textAlign: 'center', color: '#6B7280' }}>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                                    <td>{formatDateDisplay(item.date, item.label)}</td>
                                    <td>{formatNum(item.revenue)}</td>
                                    <td>{formatNum(item.cost)}</td>
                                    <td className={styles.profitCell}>{formatNum(item.profit)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <span className={styles.paginationInfo}>
                                {`${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, pagination.totalItems)} / ${pagination.totalItems}`}
                            </span>
                            <div className={styles.paginationControls}>
                                <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>
                                    {'‹'}
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                    .filter((n) => n === 1 || n === pagination.totalPages || Math.abs(n - page) <= 2)
                                    .reduce((acc, n, i, arr) => {
                                        if (i > 0 && n - arr[i - 1] > 1) acc.push('dots-' + i);
                                        acc.push(n);
                                        return acc;
                                    }, [])
                                    .map((n) =>
                                        typeof n === 'string' ? (
                                            <span key={n} className={styles.pageDots}>...</span>
                                        ) : (
                                            <button
                                                key={n}
                                                className={`${styles.pageBtn}${n === page ? ` ${styles.pageBtnActive}` : ''}`}
                                                onClick={() => setPage(n)}
                                            >
                                                {n}
                                            </button>
                                        ),
                                    )}
                                <button className={styles.pageBtn} disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                                    {'›'}
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                )}
            </div>
        </div>
    );
}
